import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useFlag } from '@modernloop/shared/feature-flag';

import {
  useResetInterviewPlanToBaseJobStageLazyQuery,
  useResetPlanToBaseJobStageMutation,
} from 'src/generated/mloop-graphql';

import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';

import cloneInterviewPlan from './cloneInterviewPlan';
import getSaveInterviewPlanInput from './getSaveInterviewPlanInput';

type Props = {
  jobStageId: string;
  customJobStageId: string;
  doNotPersist?: boolean;
};

export const useResetInterviewPlanToBaseJobStage = ({ jobStageId, customJobStageId, doNotPersist }: Props) => {
  const [fetchInterviewPlan, { loading }] = useResetInterviewPlanToBaseJobStageLazyQuery({
    variables: {
      jobStageId,
    },
  });

  const { logEvent } = useLogEvent();

  const collaborationLinkEnabled = useFlag('prd-155-codesignal-template-select');

  const [resetPlan, { loading: ld }] = useResetPlanToBaseJobStageMutation();

  const resetPlanToBaseJobStage = useCallback(async () => {
    const { data } = await fetchInterviewPlan();
    if (!data) {
      return null;
    }

    const { jobStage } = data;

    if (!jobStage) {
      return null;
    }

    logEvent(LoggerEvent.CLIENT_RESET_JOB_STAGE_PLAN, {
      type: 'Reset interview plan to job stage default',
    });

    // If doNotPersist is true, return the base job stage interview plan data
    if (doNotPersist) {
      return cloneInterviewPlan({ interviewPlan: jobStage }, { customJobStageId });
    }

    const input = getSaveInterviewPlanInput(
      { interviewPlan: jobStage },
      { finalJobStageId: customJobStageId, generateNewIds: true, collaborationLinkEnabled }
    );
    const result = await resetPlan({ variables: { input } });

    return result.data?.jobStageInterviewPlanUpsert?.jobStage;
  }, [fetchInterviewPlan, logEvent, doNotPersist, customJobStageId, resetPlan, collaborationLinkEnabled]);

  return { resetPlan: resetPlanToBaseJobStage, isLoading: loading || ld };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ResetInterviewPlanToBaseJobStageQuery = gql`
  ${getSaveInterviewPlanInput.fragments.interviewPlan}
  query ResetInterviewPlanToBaseJobStage($jobStageId: uuid!) {
    jobStage(id: $jobStageId) {
      id
      ...getSaveInterviewPlanInput_interviewPlan
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ResetInterviewPlanToBaseJobStageMutation = gql`
  ${getSaveInterviewPlanInput.fragments.interviewPlan}
  mutation ResetPlanToBaseJobStage($input: JobStageInterviewPlanUpsertInput!) {
    jobStageInterviewPlanUpsert(input: $input) {
      jobStage {
        id
        ...getSaveInterviewPlanInput_interviewPlan
      }
    }
  }
`;
